import { cloneDeep, isEqual } from "lodash";
import { readV3DivElement } from "../functions/readV3DivElement";
import { EditorV3AtBlock } from "./EditorV3AtBlock";
import { EditorV3PositionClass } from "./EditorV3Position";
import { EditorV3SelectBlock } from "./EditorV3SelectBlock";
import { EditorV3TextBlock } from "./EditorV3TextBlock";
import { defaultContentProps } from "./defaultContentProps";
import { drawHtmlDecimalAlign } from "./drawHtmlDecimalAlign";
import {
  EditorV3Align,
  EditorV3BlockClass,
  EditorV3ContentProps,
  EditorV3ContentPropsInput,
  EditorV3Position,
  EditorV3RenderProps,
  EditorV3Styles,
  EditorV3WordPosition,
  IEditorV3Line,
} from "./interface";
import { textBlockFactory } from "./textBlockFactory";

/**
 * Represents a single editable line composed of text/select/at blocks and
 * exposes manipulation utilities used by the editor (insertion, deletion,
 * styling and rendering).
 */
export class EditorV3Line implements IEditorV3Line {
  public textBlocks: EditorV3BlockClass[];
  private _defaultContentProps: EditorV3ContentProps = cloneDeep(defaultContentProps);
  public contentProps: EditorV3ContentProps;

  // #region Renderers
  /**
   * Render the line as editable HTML for the editor DOM.
   *
   * @param renderProps - Rendering options and targets used during build
   */
  public toHtml(renderProps: EditorV3RenderProps): HTMLDivElement {
    const h = document.createElement("div");
    this._setBlockStartPositions();
    renderProps.currentEl = h;
    h.className = `aiev3-line ${this.contentProps.textAlignment}`;
    if (this.contentProps.textAlignment === EditorV3Align.decimal) {
      const decimalPosition = this.lineText.match(/\./)?.index ?? Infinity;
      const upTo = this.upToPos(decimalPosition, true);
      const from = this.fromPos(decimalPosition, true);
      drawHtmlDecimalAlign(renderProps, this.contentProps, upTo, from);
    } else {
      if (this.textBlocks[0]?.isLocked) {
        const startLineEl = document.createElement("span");
        startLineEl.textContent = "\u2009";
        h.appendChild(startLineEl);
      }
      this.textBlocks.forEach((tb) =>
        h.append(
          tb.toHtml(renderProps, tb.style ? this.contentProps.styles?.[tb.style] : undefined),
        ),
      );
    }
    // Need to add a space to the end of the line to allow for the cursor to be placed at the end
    if (
      this.textBlocks.length > 0 &&
      this.textBlocks[this.textBlocks.length - 1].isLocked &&
      this.contentProps.textAlignment !== EditorV3Align.decimal
    ) {
      const endLineEl = document.createElement("span");
      endLineEl.textContent = "\u200b";
      h.append(endLineEl);
    }
    if (renderProps.editableEl) renderProps.editableEl.append(h);
    return h;
  }

  /**
   * Render the line as a markdown-style HTML div. Used for read-only or
   * markdown-mode rendering.
   *
   * @param renderProps - Rendering options and targets used during build
   */
  public toMarkdown(renderProps: EditorV3RenderProps): HTMLDivElement {
    const h = document.createElement("div");
    renderProps.currentEl = h;
    h.className = "aiev3-markdown-line";
    h.textContent = this.textBlocks
      .map((tb) =>
        tb.toMarkdown(renderProps.markdownSettings ?? this.contentProps.markdownSettings),
      )
      .join("");
    if (h.textContent === "") h.textContent = "\u200b";

    if (renderProps.editableEl) renderProps.editableEl.append(h);
    return h;
  }
  // #endregion Renderers

  // #region Getters
  /**
   * Combined text of all blocks in the line.
   */
  get lineText(): string {
    return this.textBlocks.map((tb) => tb.text).join("");
  }

  /**
   * The markdown representation of the line by concatenating block markdowns.
   */
  get lineMarkdown(): string {
    return this.textBlocks.map((tb) => tb.toMarkdown()).join("");
  }

  /**
   * Word-level positions across all blocks in this line.
   */
  get wordPositions(): EditorV3WordPosition[] {
    return this.textBlocks.flatMap((tb) => tb.wordPositions);
  }

  /**
   * Total character length of the line's text.
   */
  get lineLength(): number {
    return this.lineText.length;
  }

  /**
   * Whether any block in the line is enhanced (non-default style/label/type).
   */
  get isEnhanced(): boolean {
    return this.textBlocks.some((tb) => tb.isEnhanced);
  }

  /**
   * Serialise the line to the model shape used by the EditorV3 API.
   */
  get data(): IEditorV3Line {
    const contentProps = cloneDeep(this.contentProps);
    Object.keys(defaultContentProps).forEach((k) => {
      const key = k as keyof typeof defaultContentProps;
      if (isEqual(this.contentProps[key], this._defaultContentProps[key])) {
        delete contentProps[key];
      }
    });
    const textBlocks = this.textBlocks.map((tb) => tb.data);
    return Object.keys(contentProps).length === 0 ? { textBlocks } : { contentProps, textBlocks };
  }
  // #endregion Getters

  // #region Constructors
  // Constructor
  /**
   * Construct a line from a model object, HTML div, block array or markdown string.
   *
   * @param arg - Source value used to build the line
   * @param contentProps - Optional content property overrides
   */
  constructor(
    arg?: IEditorV3Line | HTMLDivElement | EditorV3BlockClass[] | string,
    contentProps?: EditorV3ContentPropsInput,
  ) {
    // Set defaults
    this.textBlocks = [];
    this.contentProps = cloneDeep(this._defaultContentProps);

    // Markdown HTMLDivElement
    if (arg instanceof HTMLDivElement && arg.classList.contains("aiev3-markdown-line")) {
      this.contentProps = {
        ...contentProps,
        ...this._defaultContentProps,
      };
      this.fromMarkdown(
        arg.textContent ??
          // istanbul ignore next
          "",
      );
    }
    // Standard HTMLDivElement
    else if (arg instanceof HTMLDivElement) {
      const ret = readV3DivElement(arg);
      this.textBlocks = ret.textBlocks;
      this.contentProps.textAlignment = ret.textAlignment;
      this.contentProps.decimalAlignPercent = ret.decimalAlignPercent;
      // Only need to merge after HMTL read?
      this._mergeBlocks();
    }
    // Markdown text
    else if (typeof arg === "string") {
      this.contentProps = {
        ...contentProps,
        ...this._defaultContentProps,
      };
      this.fromMarkdown(arg);
    }
    // Block array
    else if (Array.isArray(arg)) {
      this.textBlocks = arg;
      this._mergeBlocks();
    }
    // JSON object/undefined
    else {
      this.textBlocks = arg?.textBlocks.map((tb) => textBlockFactory(tb)) ?? [
        new EditorV3TextBlock(),
      ];
      this.contentProps = { ...this.contentProps, ...arg?.contentProps };
    }

    // Always take these if provided
    if (contentProps) this.contentProps = { ...this._defaultContentProps, ...contentProps };

    // Fix any problems
    this._lockTextBlocksByStyle();
    this._setBlockStartPositions();
  }

  /**
   * Parse a markdown string and populate `textBlocks` according to markdown rules.
   *
   * @param markdown - The markdown string representing this line
   */
  private fromMarkdown(markdown: string) {
    /* istanbul ignore else */
    if (this.contentProps.markdownSettings) {
      let remainingMarkdown = markdown.replace(/[\u2009-\u200f]/g, "");
      let safety = 0;
      while (remainingMarkdown.length > 0 && safety < 100) {
        safety++;
        // Start of complete select block
        if (
          remainingMarkdown.startsWith(this.contentProps.markdownSettings.dropDownStartTag) &&
          remainingMarkdown.indexOf(this.contentProps.markdownSettings.dropDownSelectedValueTag) >
            -1 &&
          remainingMarkdown.indexOf(this.contentProps.markdownSettings.dropDownEndTag) > -1
        ) {
          const blockEnd = remainingMarkdown.indexOf(
            this.contentProps.markdownSettings.dropDownEndTag,
          );
          this.textBlocks.push(
            new EditorV3SelectBlock(
              remainingMarkdown.slice(
                0,
                blockEnd + this.contentProps.markdownSettings.dropDownEndTag.length,
              ),
              { markdownSettings: this.contentProps.markdownSettings },
            ),
          );
          remainingMarkdown = remainingMarkdown.slice(
            blockEnd + this.contentProps.markdownSettings.dropDownEndTag.length,
          );
        }
        // Start of complete at block
        else if (
          remainingMarkdown.startsWith(this.contentProps.markdownSettings.atStartTag) &&
          remainingMarkdown.indexOf(this.contentProps.markdownSettings.atEndTag) > -1
        ) {
          const blockEnd = remainingMarkdown.indexOf(this.contentProps.markdownSettings.atEndTag);
          this.textBlocks.push(
            new EditorV3AtBlock(
              remainingMarkdown.slice(
                0,
                blockEnd + this.contentProps.markdownSettings.atEndTag.length,
              ),
              { markdownSettings: this.contentProps.markdownSettings },
            ),
          );
          remainingMarkdown = remainingMarkdown.slice(
            blockEnd + this.contentProps.markdownSettings.atEndTag.length,
          );
        }
        // Start of complete style block
        else if (
          remainingMarkdown.startsWith(this.contentProps.markdownSettings.styleStartTag) &&
          remainingMarkdown.indexOf(this.contentProps.markdownSettings.styleEndTag) > -1
        ) {
          const blockEnd = remainingMarkdown.indexOf(
            this.contentProps.markdownSettings.styleEndTag,
          );
          this.textBlocks.push(
            new EditorV3TextBlock(
              remainingMarkdown.slice(
                0,
                blockEnd + this.contentProps.markdownSettings.styleEndTag.length,
              ),
              { markdownSettings: this.contentProps.markdownSettings },
            ),
          );
          remainingMarkdown = remainingMarkdown.slice(
            blockEnd + this.contentProps.markdownSettings.styleEndTag.length,
          );
        }
        // Text block until next tag
        else {
          const nextDropTag = remainingMarkdown
            .slice(1)
            .indexOf(this.contentProps.markdownSettings.dropDownStartTag);
          const nextAtTag = remainingMarkdown
            .slice(1)
            .indexOf(this.contentProps.markdownSettings.atStartTag);
          const nextStyleTag = remainingMarkdown
            .slice(1)
            .indexOf(this.contentProps.markdownSettings.styleStartTag);
          const blockEnd = Math.min(
            nextDropTag > -1 ? nextDropTag + 1 : Infinity,
            nextAtTag > -1 ? nextAtTag + 1 : Infinity,
            nextStyleTag > -1 ? nextStyleTag + 1 : Infinity,
            remainingMarkdown.length,
          );
          this.textBlocks.push(
            new EditorV3TextBlock(remainingMarkdown.slice(0, blockEnd), {
              markdownSettings: this.contentProps.markdownSettings,
            }),
          );
          remainingMarkdown = remainingMarkdown.slice(blockEnd);
        }
      }
    }
  }
  // #endregion Constructors

  // #region Get/Set block methods
  /**
   * Return the block containing a given character position.
   *
   * @param pos - Character index within the line
   */
  public getBlockAt(pos: number): EditorV3BlockClass | undefined {
    return this.textBlocks.find((tb) => tb.lineStartPosition <= pos && tb.lineEndPosition > pos);
  }

  /**
   * Return the style name applied at a character position.
   *
   * @param pos - Character index within the line
   */
  public getStyleAt(pos: number): string | undefined {
    return this.getBlockAt(pos)?.style;
  }

  /**
   * Return the names of all the styles used in the line.
   */
  public getAllStyles(): string[] {
    const stylesSet = new Set<string>();
    this.textBlocks.forEach((tb) => {
      if (tb.style) stylesSet.add(tb.style);
    });
    return Array.from(stylesSet);
  }

  /**
   * Set the active block according to a caret/selection position.
   *
   * @param pos - Position object describing the caret or selection
   */
  public setActiveBlock(
    pos: EditorV3Position | EditorV3PositionClass,
  ): EditorV3BlockClass | undefined {
    this.textBlocks.forEach((tb) => tb.setActive(false));
    const nextBlock = this.getBlockAt(pos.startChar);
    const lastBlock = this.getBlockAt(Math.max(0, pos.startChar - 1));
    if (lastBlock && lastBlock instanceof EditorV3AtBlock && pos.isCollapsed) {
      lastBlock.setActive(true);
      return lastBlock;
    } else if (nextBlock && nextBlock instanceof EditorV3SelectBlock) {
      nextBlock.showDropdown();
      return nextBlock;
    } else {
      nextBlock?.setActive(true);
      return nextBlock;
    }
  }
  // #endregion Get/Set block methods

  // #region Subset methods
  /**
   * Extract a list of blocks representing a subset of the line between
   * `startPos` and `endPosOpt`. Optionally split locked blocks.
   *
   * @param startPos - Start character index
   * @param endPosOpt - Optional end character index
   * @param splitLocked - When true, locked blocks may be split to fit
   */
  public subBlocks(
    startPos: number,
    endPosOpt?: number,
    splitLocked = false,
  ): EditorV3BlockClass[] {
    const ret: EditorV3BlockClass[] = [];
    const endPos = endPosOpt ?? this.lineLength + 1;
    // Return empty styled block for start/end same
    if (startPos >= endPos || endPos <= 0) {
      return ret;
    }
    for (let _i = 0; _i < this.textBlocks.length; _i++) {
      const block: EditorV3BlockClass = this.textBlocks[_i];
      // Block after start and before end
      if (startPos <= block.lineStartPosition && block.lineEndPosition < endPos) {
        ret.push(cloneDeep(block));
      }
      // Block containing startPos and is not locked/being split
      else if (
        block.lineStartPosition < startPos &&
        block.lineEndPosition > startPos &&
        (!block.isLocked || splitLocked)
      ) {
        const slicedBlock = textBlockFactory({
          ...block.data,
          // Split locked occurs on decimals, so lineStartPosition is needed
          lineStartPosition: splitLocked ? block.lineStartPosition : undefined,
          text: block.text.slice(
            startPos - block.lineStartPosition,
            endPos - block.lineStartPosition,
          ),
        });
        ret.push(slicedBlock);
      }
      // Block after start containing end
      else if (
        block.lineStartPosition >= startPos &&
        block.lineStartPosition < endPos &&
        block.lineEndPosition >= endPos
      ) {
        if (block.isLocked && !splitLocked) {
          ret.push(cloneDeep(block));
        } else {
          const slicedBlock = textBlockFactory({
            ...block,
            text: block.text.slice(0, endPos - block.lineStartPosition),
          });
          ret.push(slicedBlock);
        }
      }
      // Stop if the end is reached
      if (block.lineStartPosition >= endPos) break;
    }
    if (!splitLocked) this._setBlockStartPositions(ret, startPos);
    return ret;
  }

  /**
   * Return blocks from the start of the line up to `pos`.
   *
   * @param pos - Character index to cut off at
   * @param splitLocked - Whether locked blocks can be split
   */
  public upToPos(pos: number, splitLocked = false): EditorV3BlockClass[] {
    return this.subBlocks(0, pos, splitLocked);
  }

  /**
   * Return blocks from `pos` to the end of the line.
   *
   * @param pos - Character index to start from
   * @param splitLocked - Whether locked blocks can be split
   */
  public fromPos(pos: number, splitLocked = false): EditorV3BlockClass[] {
    return this.subBlocks(pos, undefined, splitLocked);
  }
  // #endregion Subset methods

  // #region Insert and remove methods
  /**
   * Split the line at `pos` returning the tail as a new `EditorV3Line`.
   *
   * @param pos - Character index where the split occurs
   */
  public splitLine(pos: number): EditorV3Line {
    if (pos >= this.lineLength) {
      return new EditorV3Line(undefined, this.contentProps);
    } else {
      const preSplit = this.upToPos(pos);
      const postSplit = this.fromPos(pos);
      this.textBlocks = preSplit.length > 0 ? preSplit : [new EditorV3TextBlock()];
      this._setBlockStartPositions();
      return new EditorV3Line(postSplit, this.contentProps);
    }
  }

  /**
   * Insert blocks at a character position within the line.
   *
   * @param newTextBlocks - Blocks to insert
   * @param pos - Character index where insertion begins
   */
  public insertBlocks(newTextBlocks: EditorV3BlockClass[], pos: number) {
    const pre = this.upToPos(pos);
    const post = this.fromPos(pos);
    this.textBlocks = [...pre, ...newTextBlocks, ...post];
    this._mergeBlocks();
    this._setBlockStartPositions();
  }

  /**
   * Remove a section of the line between `startPos` and `endPos` and
   * return the removed blocks.
   *
   * @param startPos - Starting character index (inclusive)
   * @param endPos - Ending character index (exclusive)
   */
  public removeSection(startPos: number, endPos: number): EditorV3BlockClass[] {
    let ret: EditorV3BlockClass[] = [];
    if (startPos < endPos && startPos < this.lineLength) {
      const pre = this.upToPos(startPos);
      ret = this.subBlocks(startPos, endPos);
      const post = this.fromPos(endPos);
      this.textBlocks = [...pre, ...post];
      this._mergeBlocks();
      this._setBlockStartPositions();
    }
    return ret;
  }

  /**
   * Delete a single character at position `pos` and return the mutated line.
   *
   * @param pos - Character index to delete
   */
  public deleteCharacter(pos: number) {
    this.removeSection(pos, pos + 1);
    return this;
  }
  // #endregion Insert and remove methods

  // #region Update style methods
  /**
   * Apply a named style to a range of characters in the line.
   *
   * @param styleName - Style identifier to apply
   * @param startPos - Start character index
   * @param endPos - End character index
   */
  public applyStyle(styleName: string, startPos: number, endPos: number) {
    if (startPos < endPos) {
      const upTo = this.upToPos(startPos);
      const mid = this.subBlocks(startPos, endPos).map((tb) => {
        tb.style = styleName;
        if (this.contentProps.styles?.[styleName]?.isLocked) tb.isLocked = true;
        return tb;
      });
      const from = this.fromPos(endPos);
      this.textBlocks = [...upTo, ...mid, ...from];
      this._mergeBlocks();
      this._setBlockStartPositions();
    }
    return this;
  }

  /**
   * Remove style information from blocks in the supplied range.
   *
   * @param startPos - Start character index
   * @param endPos - End character index
   */
  public removeStyle(startPos: number, endPos: number) {
    if (startPos < endPos) {
      this.textBlocks = [
        ...this.upToPos(startPos),
        ...this.subBlocks(startPos, endPos).map((tb) => {
          tb.style = undefined;
          if (tb.isLocked) delete tb.isLocked;
          return tb;
        }),
        ...this.fromPos(endPos),
      ];
      this._mergeBlocks();
      this._setBlockStartPositions();
    }
    return this;
  }

  /**
   * Remove the style information from all blocks that have any of the specified styles.
   *
   * @param styleNames - Array of style names to remove
   * @returns The updated line instance
   */
  public cleanStyles(styleNames: string[]) {
    this.textBlocks = this.textBlocks.map((tb) => {
      if (tb.style && styleNames.includes(tb.style)) {
        tb.style = undefined;
        if (tb.isLocked) delete tb.isLocked;
      }
      return tb;
    });
    this._mergeBlocks();
    this._setBlockStartPositions();
    return this;
  }

  /**
   * Delete text blocks that have any of the specified styles.
   *
   * @param styleNames - Array of style names to delete
   * @returns The updated line instance
   */
  public deleteStyles(styleNames: string[]) {
    this.textBlocks = this.textBlocks.filter((tb) => styleNames.includes(tb.style ?? "") === false);
    if (this.textBlocks.length === 0) {
      this.textBlocks = [new EditorV3TextBlock()];
    }
    this._mergeBlocks();
    this._setBlockStartPositions();
    return this;
  }
  // #endregion Update style methods

  // #region Private methods
  /**
   * Merge adjacent blocks that share the same merge key into single blocks.
   */
  private _mergeBlocks() {
    if (new Set(this.textBlocks.map((tb) => tb.mergeKey)).size < this.textBlocks.length) {
      const mergedBlocks: EditorV3BlockClass[] = [];
      let lastTypeStyle: string | null = null;
      for (let _i = 0; _i < this.textBlocks.length; _i++) {
        if (this.textBlocks[_i].mergeKey === lastTypeStyle && mergedBlocks.length > 0) {
          mergedBlocks[mergedBlocks.length - 1] = textBlockFactory({
            ...mergedBlocks[mergedBlocks.length - 1].data,
            text: mergedBlocks[mergedBlocks.length - 1].text + this.textBlocks[_i].text,
          });
        } else {
          mergedBlocks.push(this.textBlocks[_i]);
          lastTypeStyle = this.textBlocks[_i].mergeKey;
        }
      }
      this.textBlocks = mergedBlocks.filter((tb) => tb.text !== "");
    }
  }

  /**
   * Apply `isLocked` to blocks when their style is defined as locked in
   * the content properties.
   */
  private _lockTextBlocksByStyle() {
    // Check for any locked styles
    const lockedStyles = Object.keys(this.contentProps.styles ?? []).filter(
      (k) => (this.contentProps.styles as EditorV3Styles)[k].isLocked,
    );
    if (lockedStyles.length > 0)
      this.textBlocks.forEach((tb) => {
        if (lockedStyles.includes(tb.style ?? "")) tb.isLocked = true;
      });
  }

  /**
   * Calculate and set `lineStartPosition` for each block, optionally for a
   * provided subset starting at `initialPos`.
   *
   * @param blocks - Optional block array to update (defaults to this.textBlocks)
   * @param initialPos - Starting character position for the first block
   */
  private _setBlockStartPositions(blocks?: EditorV3BlockClass[], initialPos: number = 0) {
    let _linePos = initialPos;
    const tbs = blocks ?? this.textBlocks;
    tbs.forEach((tb) => {
      tb.lineStartPosition = _linePos;
      /* istanbul ignore else */
      if (tb.lineEndPosition !== undefined) {
        _linePos = tb.lineEndPosition;
      }
    });
  }
  // #endregion Private methods
}
